import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { DollarSign, ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showOpeningCashDialog, setShowOpeningCashDialog] = useState(false);
  const [openingCash, setOpeningCash] = useState('');
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [authToken, setAuthToken] = useState(null);
  const [lastClosingCash, setLastClosingCash] = useState(null);
  const [lastClosingDate, setLastClosingDate] = useState(null);
  const [lastClosingUser, setLastClosingUser] = useState(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  // Fetch last closing cash when dialog appears
  useEffect(() => {
    if (showOpeningCashDialog && authToken) {
      fetchLastClosingCash();
    }
  }, [showOpeningCashDialog, authToken]);

  const fetchLastClosingCash = async () => {
    try {
      // Fetch last closed session (any user) for carry-forward
      const response = await fetch(`${API_URL}/api/sessions/last-closing`, {
        headers: { 'Authorization': `Bearer ${authToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        if (data.closing_cash !== null) {
          setLastClosingCash(data.closing_cash);
          setLastClosingDate(data.ended_at);
          setLastClosingUser(data.username);
          setOpeningCash(data.closing_cash.toFixed(2));
        }
      }
    } catch (error) {
      console.error('Failed to fetch session data:', error);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!username.trim() || !password.trim()) {
      toast({
        title: "Error",
        description: "Please enter both username and password",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Login failed');
      }

      const data = await response.json();
      
      // Admin bypasses opening cash - goes directly to dashboard
      if (data.user.role === 'admin') {
        localStorage.setItem('isAuthenticated', 'true');
        localStorage.setItem('authToken', data.token);
        localStorage.setItem('username', data.user.username);
        localStorage.setItem('userRole', data.user.role);
        localStorage.setItem('userName', data.user.name);
        localStorage.setItem('userId', data.user.id);
        localStorage.setItem('sessionStartTime', new Date().toISOString());
        
        toast({
          title: "Success",
          description: `Welcome, ${data.user.name}!`
        });
        navigate('/dashboard');
      } else {
        // Staff users need to enter opening cash
        setLoggedInUser(data.user);
        setAuthToken(data.token);
        setShowOpeningCashDialog(true);
      }
    } catch (error) {
      toast({
        title: "Login Failed",
        description: error.message || "Invalid username or password",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpeningCashSubmit = async () => {
    const cashValue = parseFloat(openingCash);
    
    if (isNaN(cashValue) || openingCash.trim() === '') {
      toast({
        title: "Error",
        description: "Please enter a valid opening cash amount",
        variant: "destructive"
      });
      return;
    }

    if (openingCash.includes('.') && openingCash.split('.')[1]?.length > 2) {
      toast({
        title: "Error",
        description: "Opening cash can only have up to 2 decimal places",
        variant: "destructive"
      });
      return;
    }

    try {
      // Start session in backend
      const response = await fetch(`${API_URL}/api/sessions/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify({ opening_cash: cashValue }),
      });

      if (!response.ok) {
        throw new Error('Failed to start session');
      }

      const sessionData = await response.json();

      // Save session data locally
      localStorage.setItem('isAuthenticated', 'true');
      localStorage.setItem('authToken', authToken);
      localStorage.setItem('username', loggedInUser.username);
      localStorage.setItem('userRole', loggedInUser.role);
      localStorage.setItem('userName', loggedInUser.name);
      localStorage.setItem('userId', loggedInUser.id);
      localStorage.setItem('openingCash', cashValue.toFixed(2));
      localStorage.setItem('sessionId', sessionData.id);
      localStorage.setItem('sessionStartTime', sessionData.started_at);

      toast({
        title: "Success",
        description: `Welcome, ${loggedInUser.name}!`
      });

      setShowOpeningCashDialog(false);
      navigate('/dashboard');
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to start session. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleOpeningCashChange = (e) => {
    const value = e.target.value;
    if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
      setOpeningCash(value);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#e8ecf0' }}>
      <Card className="w-full max-w-md bg-white shadow-lg rounded-lg p-8">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_6a116685-1875-4e0c-97f5-a9213e7bf53a/artifacts/isz5lodv_IMG_6110.PNG" 
            alt="New Shop Logo" 
            className="h-20 object-contain"
            data-testid="login-logo"
          />
        </div>

        {/* Login Form */}
        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <Input
              type="text"
              placeholder="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full h-12 px-4 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400"
              data-testid="login-username"
            />
          </div>

          <div>
            <Input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full h-12 px-4 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400"
              data-testid="login-password"
            />
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-12 bg-yellow-400 hover:bg-yellow-500 text-black font-semibold text-sm tracking-wider rounded-md transition-colors uppercase"
            data-testid="login-submit"
          >
            {loading ? 'LOGGING IN...' : 'LOGIN'}
          </Button>
        </form>
      </Card>

      {/* Opening Cash Dialog */}
      <Dialog open={showOpeningCashDialog} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center space-x-2">
              <DollarSign className="w-6 h-6 text-green-600" />
              <span>Opening Cash</span>
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-600 mb-4">
              Welcome, <span className="font-semibold">{loggedInUser?.name}</span>! 
              Please enter the opening cash amount to start your session.
            </p>
            
            {/* Carry Forward Info */}
            {lastClosingCash !== null && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-blue-800">Previous Closing Cash</p>
                    <p className="text-xs text-blue-600">
                      by {lastClosingUser || 'Unknown'} {lastClosingDate && (' - ' + new Date(lastClosingDate).toLocaleDateString('en-IN', { 
                        day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
                      }))}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold text-blue-700">₹{lastClosingCash.toFixed(2)}</p>
                    <p className="text-xs text-blue-600 flex items-center">
                      Carry Forward <ArrowRight className="w-3 h-3 ml-1" />
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            <div>
              <Label htmlFor="openingCash" className="text-sm font-medium">Opening Cash (₹)</Label>
              <div className="relative mt-2">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">₹</span>
                <Input
                  id="openingCash"
                  type="text"
                  inputMode="decimal"
                  placeholder="0.00"
                  value={openingCash}
                  onChange={handleOpeningCashChange}
                  className="pl-8 text-lg font-semibold"
                  autoFocus
                  data-testid="opening-cash-input"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">Enter amount with up to 2 decimal places</p>
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={handleOpeningCashSubmit}
              className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold"
              data-testid="opening-cash-submit"
            >
              Start Session
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Login;
